import { useState } from "react";
import { api, money } from "../lib/api";
import { toast } from "sonner";
import { Wallet, CreditCard } from "@phosphor-icons/react";
import TyroPaymentDialog from "./TyroPaymentDialog";
import PinPromptModal from "./PinPromptModal";
import { CreditCustomerPicker } from "./CreditCustomerPicker";

export default function RefundModal({ invoice, user, onClose, onDone, prefillAmount }) {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(prefillAmount ? String(prefillAmount) : "");
  const [reason, setReason] = useState(prefillAmount ? "Overpayment — price reduced after payment" : "");
  const [saving, setSaving] = useState(false);
  const [tyroOpen, setTyroOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pendingMethod, setPendingMethod] = useState(null);
  const [pendingTyroMeta, setPendingTyroMeta] = useState(null);
  const [creditCustomer, setCreditCustomer] = useState(null);
  const hasCustomer = !!invoice.customer_id;
  const canSms = !!invoice.customer_phone;
  const canEmail = !!invoice.customer_email;
  const [notify, setNotify] = useState({ sms: canSms, email: false });
  const notifyVia = notify.sms && notify.email ? "both" : notify.sms ? "sms" : notify.email ? "email" : null;

  const payments = invoice.payment_history || [];
  const totalPaid = payments.reduce((s, p) => s + (p.is_refund || p.corrected_at ? 0 : p.amount), 0);
  const totalRefunded = payments.reduce((s, p) => s + (p.is_refund ? Math.abs(p.amount) : 0), 0);
  const stripeRefundable = Math.max(0, parseFloat(invoice.stripe_refundable || 0));
  const allRefundable = Math.max(0, totalPaid - totalRefunded);
  const maxRefundable = method === "stripe" ? Math.min(allRefundable, stripeRefundable) : allRefundable;

  const processRefund = async (pin) => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error("Enter a valid amount");
    if (val > maxRefundable + 0.01) return toast.error(`Max refundable is $${money(maxRefundable)}`);
    setSaving(true);
    try {
      // POS sales with no online invoice refund through the sale itself.
      const isSale = !!invoice._is_sale;
      const payload = { method: isSale && pendingMethod === "card" ? "card_manual" : pendingMethod, amount: val, reason };
      if (pendingTyroMeta) payload.tyro_meta = pendingTyroMeta;
      if (pendingMethod === "store_credit") payload.customer_id = creditCustomer?.id || invoice.customer_id;
      if (notifyVia) payload.notify = notifyVia;
      const header = user?.role === "admin" ? "X-Admin-Pin" : "X-Staff-Pin";
      const url = isSale ? `/sales/${invoice.id}/refund` : `/online-invoices/${invoice.id}/refund`;
      const { data } = await api.post(url, payload, { headers: { [header]: pin } });
      const n = data?.notification;
      if (n) {
        if (["sms_status", "email_status"].some((k) => n[k] === "sent")) toast.success("Refund confirmation sent to the customer");
        (n.errors || []).forEach((m) => toast.error(m));
      }
      if (pendingMethod === "store_credit") {
        const slip = isSale ? { sale_id: invoice.id, refund_index: data?.refund_index } : data?.last_refund;
        const printSlip = () => slip && slip.refund_index != null && api.post(`/print/refund/${slip.sale_id}/${slip.refund_index}`, {})
          .then(() => toast.success("Store credit slip sent to printer"))
          .catch((e) => {
            if (e?.response?.status === 412) return;
            toast.error("Slip didn't print — printer unavailable. Use Reprint slip to try again.", { id: "credit-slip-print" });
          });
        toast.success(`$${val.toFixed(2)} added to ${creditCustomer?.name || invoice.customer_name}'s store credit`, {
          duration: 8000,
          action: slip ? { label: "Reprint slip", onClick: printSlip } : undefined,
        });
        printSlip();
      } else if (pendingMethod === "stripe") {
        toast.success(`$${val.toFixed(2)} refunded to the customer's card — usually shows in 5-10 business days`);
      } else {
        toast.success(`$${val.toFixed(2)} ${pendingMethod} refund processed`);
      }
      onDone();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error("Enter a valid amount");
    if (method === "store_credit" && !hasCustomer && !creditCustomer) return toast.error("Pick a customer to hold the store credit");
    if (method === "tyro") {
      setTyroOpen(true);
      return;
    }
    setPendingMethod(method);
    setPendingTyroMeta(null);
    setPinOpen(true);
  };

  const handleTyroApproved = (meta) => {
    setTyroOpen(false);
    setPendingMethod("tyro");
    setPendingTyroMeta(meta);
    setPinOpen(true);
  };

  return (
    <>
      <div className="fixed inset-0 z-[10000] bg-slate-900/60 flex items-center justify-center p-3" onClick={onClose}>
        <div
          className="bg-white w-full max-w-md rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="refund-modal"
        >
          <div className="px-6 pt-6 pb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">REFUND</div>
            <h3 className="font-heading font-bold text-[18px] text-slate-900 leading-tight mt-0.5">
              Refund {invoice.invoice_number}
            </h3>
            <div className="text-[12px] text-slate-500 mt-0.5">
              {invoice.customer_name || "Walk-in"} · total ${money(invoice.total)}
            </div>
            <div className="mt-3 rounded-xl border-2 px-3 py-2 flex items-baseline justify-between"
                 style={{ background: "linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)", borderColor: "#DC2626" }}>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#DC2626]">Max refundable</span>
              <span className="font-mono font-bold text-[20px] text-[#DC2626] tabular-nums">${money(maxRefundable)}</span>
            </div>
          </div>

          <div className="px-6 pb-6 space-y-3">
            {/* Amount */}
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Amount</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-mono">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={maxRefundable}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full h-12 pl-7 pr-3 rounded-xl border border-slate-200 focus:border-[#DC2626] focus:ring-2 focus:ring-red-100 focus:outline-none text-lg font-mono font-semibold"
                  data-testid="refund-amount"
                  autoFocus
                />
              </div>
              <div className="flex gap-1.5 mt-2">
                <button type="button" onClick={() => setAmount(maxRefundable.toFixed(2))} className="flex-1 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-700">FULL</button>
                <button type="button" onClick={() => setAmount((maxRefundable / 2).toFixed(2))} className="flex-1 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-700">HALF</button>
                {[20, 50].map((v) => (
                  <button key={v} type="button" onClick={() => setAmount(String(Math.min(v, maxRefundable)))} className="flex-1 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-mono font-semibold text-slate-700">${v}</button>
                ))}
              </div>
            </label>

            {/* Method — same grid style as AddPaymentModal */}
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Refund Method</div>
              <div className="grid grid-cols-4 gap-2">
                {["cash", "card", "tyro", "other"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    data-testid={`refund-method-${m}`}
                    className={`h-10 rounded-lg text-[11px] tracking-wider font-semibold transition-all flex items-center justify-center gap-1.5 ${
                      method === m
                        ? "bg-[#121216] text-white"
                        : "bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#121216]"
                    }`}
                  >
                    {m === "tyro" ? (
                      <>
                        <span>TYRO</span>
                        <img
                          src="/tyro-logo.gif"
                          alt=""
                          className="h-3 w-auto"
                          style={method === m ? { filter: "invert(1) brightness(2)" } : undefined}
                        />
                      </>
                    ) : m === "card" ? "CARD" : m.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setMethod("store_credit")}
                data-testid="refund-method-store_credit"
                aria-pressed={method === "store_credit"}
                className={`mt-2 w-full h-12 rounded-xl flex items-center justify-center gap-2.5 text-[12px] tracking-[0.14em] font-bold uppercase border-2 transition-[background-color,border-color,color,box-shadow] duration-150 ${
                  method === "store_credit"
                    ? "bg-emerald-600 border-emerald-600 text-white shadow-[0_6px_16px_-6px_rgba(5,150,105,0.6)]"
                    : "bg-emerald-50 border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                }`}
              >
                <Wallet size={18} weight={method === "store_credit" ? "fill" : "duotone"} />
                Store Credit
              </button>
            </label>

            {stripeRefundable > 0.005 && (
              <button
                type="button"
                onClick={() => {
                  setMethod("stripe");
                  const cap = Math.min(allRefundable, stripeRefundable);
                  if (parseFloat(amount) > cap) setAmount(cap.toFixed(2));
                }}
                data-testid="refund-method-stripe"
                aria-pressed={method === "stripe"}
                className={`w-full h-12 rounded-xl flex items-center justify-between px-4 text-[12px] font-bold border-2 transition-[background-color,border-color,color,box-shadow] duration-150 ${
                  method === "stripe"
                    ? "bg-[#1E5FEB] border-[#1E5FEB] text-white shadow-[0_6px_16px_-6px_rgba(30,95,235,0.6)]"
                    : "bg-[#EFF6FF] border-[#93C5FD] text-[#1E5FEB] hover:bg-[#DBEAFE]"
                }`}
              >
                <span className="flex items-center gap-2.5 uppercase tracking-[0.14em]"><CreditCard size={18} weight="bold" /> Back to card (Stripe)</span>
                <span className="font-mono tabular-nums">up to ${money(stripeRefundable)}</span>
              </button>
            )}
            {parseFloat(amount) > maxRefundable + 0.01 && (
              <div className="text-[12px] font-semibold text-rose-600" data-testid="refund-over-cap">
                Max ${money(maxRefundable)}{method === "stripe" ? " can go back to the card" : " can be refunded"}
              </div>
            )}

            {method === "store_credit" && (
              <div data-testid="store-credit-section">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Credit goes to</div>
                {hasCustomer ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] font-semibold text-emerald-900" data-testid="credit-customer-fixed">
                    {invoice.customer_name}
                  </div>
                ) : (
                  <CreditCustomerPicker value={creditCustomer} onChange={setCreditCustomer} />
                )}
              </div>
            )}

            {/* Customer confirmation */}
            {(canSms || canEmail) && (
              <div data-testid="refund-notify">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Send refund confirmation</div>
                <div className="flex gap-2">
                  {[["sms", "SMS", canSms], ["email", "Email", canEmail]].filter(([, , ok]) => ok).map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setNotify((v) => ({ ...v, [k]: !v[k] }))}
                            aria-pressed={notify[k]} data-testid={`refund-notify-${k}`}
                            className={`flex-1 h-9 rounded-lg text-[11px] font-semibold tracking-wider border transition-colors ${
                              notify[k] ? "bg-[#EFF6FF] border-[#93C5FD] text-[#1E5FEB]" : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-slate-400"
                            }`}>
                      {notify[k] ? "✓ " : ""}{label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reason */}
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Reason (optional)</div>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Defective product, customer request..."
                className="w-full h-10 px-3 rounded-xl border border-slate-200 focus:border-[#DC2626] focus:ring-2 focus:ring-red-100 focus:outline-none text-[13px]"
                data-testid="refund-reason"
              />
            </label>

            {/* Action buttons */}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold">CANCEL</button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || !amount || parseFloat(amount) <= 0 || parseFloat(amount) > maxRefundable + 0.01}
                data-testid="confirm-refund"
                className="flex-1 h-11 rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-200 disabled:text-slate-400 text-white text-[13px] font-semibold shadow-md shadow-red-500/25"
              >
                {saving ? "PROCESSING..." : "PROCESS REFUND"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tyro EFTPOS terminal dialog */}
      {tyroOpen && (
        <TyroPaymentDialog
          mode="refund"
          amountCents={Math.round(parseFloat(amount || 0) * 100)}
          onClose={() => setTyroOpen(false)}
          onApproved={handleTyroApproved}
        />
      )}

      <PinPromptModal
        open={pinOpen}
        title="Confirm refund"
        description={`Enter your PIN to refund $${money(parseFloat(amount || 0))} via ${(pendingMethod || method) === "stripe" ? "Stripe, back to the customer's card" : (pendingMethod || method)}.`}
        confirmLabel="CONFIRM REFUND"
        destructive
        statusEndpoint={user?.role === "admin" ? "/users/me/pin-status" : "/staff-pin/status"}
        onClose={() => setPinOpen(false)}
        onConfirm={async (pin) => { await processRefund(pin); setPinOpen(false); }}
      />
    </>
  );
}
